import has from 'lodash/has';
import startsWith from 'lodash/startsWith';
import { default as lodashIsString } from 'lodash/isString';
import { default as lodashOrderBy } from 'lodash/orderBy';
import { default as lodashFilter } from 'lodash/filter';
import { default as lodashFirst } from 'lodash/first';
import { default as lodashIsArray } from 'lodash/isArray';
import { default as lodashRange } from 'lodash/range';
import { default as lodashLast } from 'lodash/last';
import { default as lodashIsEmpty } from 'lodash/isEmpty';
import { default as lodashIsDate } from 'lodash/isDate';
import { default as lodashFind } from 'lodash/find';
import { default as lodashIsFunction } from 'lodash/isFunction';
import pluralize from 'pluralize';
import * as Bixby from '$/core';
import dayjs from 'dayjs';
import { AnalyticsService } from '$/services';

export const isNullOrEmpty = (value: any) => {
  return isNull(value) || isUndefined(value) || lodashIsEmpty(value);
};

export const areDefined = (...args: any[]) => {
  return args.every(arg => isDefined(arg));
};

export const isDefined = (value: any) => {
  return !isNull(value) && !isUndefined(value);
};

export const isFunctionDefined = (value: any) => {
  return typeof value === 'function';
};

export const isNull = (value: any) => {
  return value === null && typeof value === 'object';
};

export const isUndefined = (value: any) => {
  return value === undefined && typeof value === 'undefined';
};

export const arrayIsPopulated = (value: any[] | null | undefined): boolean => {
  return isDefined(value) && hasProperty(value, 'length') && (value?.length ?? -1) > 0;
};

export const hasProperties = (value: any, keys: string[]) => keys.every(key => hasProperty(value, key));

export const hasProperty = (value: any, key: string) => isDefined(value) && has(value, key);

export const errorHasStatus = (status: number, error: Bixby.Models.BixbyError) => {
  return error && error.response && error.response.status.toString().indexOf(status.toString()) >= 0;
};

/**
 *
 * @param value search text to match to target
 * @param target string to match `value` to
 */
export const searchMatches = (value: string, target: string) => {
  return startsWith(target.toLowerCase(), value.toLowerCase());
};

export const orderBy = <T>(array: any | null | undefined, iteratee?: any, orders?: any) => {
  return lodashOrderBy(array, iteratee, orders) as T[];
};

export const pluralizeStr = (count: number, text: string, inclusive?: boolean) => {
  return pluralize(text, count, !!inclusive);
};

export const isString = (str: any) => lodashIsString(str);

export const parseBoolean = (input: string | boolean) => isDefined(input) && input.toString().toLowerCase() === 'true';

export const isArray = (value?: any) => lodashIsArray(value);

export const filter = lodashFilter;

export const first = (array?: any[]) => lodashFirst(array);

export const find = <T>(collection: T[], predicate: (value: T) => boolean) => lodashFind(collection, predicate);

export const last = (array?: any[]) => lodashLast(array);

export const range = (start: number, end?: number | undefined, step?: number | undefined) =>
  lodashRange(start, end, step);

/**
 * Measures approximate pixels for a given string
 * @param str string to measure
 * @param fillChar character for measuring, defaults to G
 */
export const approximatePixles = (str: string, fillChar = 'G') => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return 0;
  }
  const arr = Array(str.length).fill(fillChar).join('');
  context.font = `12px Arial`;
  const metrics = context.measureText(arr);
  const width = metrics.width;
  return width;
};

export const toOrderString = (int: number) => {
  if (int === 1) {
    return 'Primary';
  } else if (int === 2) {
    return 'Secondary';
  } else if (int === 3) {
    return 'Tertiary';
  } else if (int === 4) {
    return 'Quaternary';
  } else if (int === 5) {
    return 'Quinary';
  } else if (int === 6) {
    return 'Senary';
  } else if (int === 7) {
    return 'Septenary';
  } else if (int === 8) {
    return 'Octonary';
  } else if (int === 9) {
    return 'Nonary';
  } else {
    return null;
  }
};

export const objectToQueryString = (params: any) => {
  const queryString = Object.keys(params)
    .map(key => key + '=' + params[key])
    .join('&');
  return `?${queryString}`;
};

export const trimName = (firstName: string, lastName: string) => {
  return `${firstName} ${lastName}`.trim();
};

export const dateToString = (date: Date) => {
  return dateToStringFormat(date, 'MM-DD-YYYY');
};

export const dateToStringFormat = (date: Date, format: string) => {
  return dayjs(date).format(format).toString();
};

export const dateIsToday: (date: Date) => boolean = (date: Date) => {
  return dayjs().format("MM-DD-YYYY") === dayjs(date).format("MM-DD-YYYY");
}

export const utcDateToString = (date: Date) => {
  return utcDateToStringFormat(date, 'MM-DD-YYYY');
};

export const utcDateToStringFormat = (date: Date, format: string) => {
  return dayjs.utc(date).format(format).toString();
};

export const utcDateAsLocal = (date: Date) => {
  return dayjs(date, { utc: true }).local();
};

export const isDate = (date: any) => {
  return lodashIsDate(date);
};

export const formatDate = (d: any) => {
  return isDate(d) ? d.toLocaleDateString() : new Date(d.toString()).toLocaleDateString();
};

export const formatDateTime = (d: dayjs.Dayjs) => {
  return d.local().format('MM/DD/YY HH:mm:ss');
};

export const formatDateForByline = (d: dayjs.Dayjs) => {
  return d.local().format('MM/DD/YY');
};

export const formatTimeForByline = (d: dayjs.Dayjs) => {
  return d.local().format('hh:mm A');
};

export const formatMonthYear = (date: Date) => {
  return dayjs.utc(date).format('MMM-YY').toString().toUpperCase();
};

export const parseUtcDateFromApi = (d: string) => {
  if (!d) {
    return null;
  }

  const date: dayjs.Dayjs = dayjs.utc(d);
  if (!date.isValid()) {
    return null;
  } else {
    return date.toDate();
  }
};

export const isFunction = (value: any) => {
  return lodashIsFunction(value);
};

// used with portfolio and capital structure
export const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }

  const parsedValue: number = parseFloat(value as any);
  if (isNaN(parsedValue)) {
    return '';
  }

  // add in appropriate formatting
  let prefix = '$';
  let suffix = '';
  if (parsedValue < 0) {
    prefix = prefix + '(';
    suffix = suffix + ')';
  }

  // remove hyphen
  const fixedParsedValue = parsedValue.toFixed(2).replace('-', '');

  const currency: string = fixedParsedValue.replace(/\d(?=(\d{3})+\.)/g, '$&,').split('.')[0];

  return prefix + currency + suffix;
};

export const formatMultiplier = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }
  const percentage = value.toFixed(1);
  return value ? `${percentage}x` : '';
};

export const formatPercentage = (value: number | null | undefined) => {
  if (value === null || value === undefined) {
    return '';
  }
  const percentage = (value * 100).toFixed(0);
  return value ? `${percentage}%` : '';
};

export const formatLeverage = (value: number | null | undefined, index: number) => {
  if (value === null || value === undefined) {
    return '';
  }

  switch (index) {
    case 0:
      return formatCurrency(value);

    case 1:
      if (value && isNaN(parseFloat(value as any))) {
        return value as any;
      } else {
        return formatPercentage(value);
      }

    default:
      return formatMultiplier(value);
  }
};

export const formatMoney = (value: string | number | null) => {
  if (!value) {
    return null;
  }
  const numberString = typeof value === 'number' ? value.toString() : value;
  const parsedValue = parseFloat(numberString.replace(/\$/g, '').replace(/,/g, ''));
  return isNaN(parsedValue) ? null : parsedValue.toFixed(2);
};

export const formatSpread  = (value: string) => {

  const spreadPercentageRegex = /^([0-9]+|[0-9]+[.][0-9]+)%$/g;
  const spreadRegex = /^([A-Za-z]\+([0-9]+|[0-9]+[.][0-9]+))$/g;
  const spreadAlphaRegex = /([^0-9.]+)?/g;

  try {
    if (!value) {
      return null;
    }

    const percentageMatched = value.match(spreadPercentageRegex);

    if (percentageMatched) {
      const percentage = parseInt(value.replaceAll('%', '')) * 100;
      return Number(percentage);
    } else {
      const spreadMatched = value.match(spreadRegex);
      if (spreadMatched) {
        const spread = value.replace(spreadAlphaRegex, '');
        return Number(spread);
      }
    }

    return null;
  } catch (e: any) {
    AnalyticsService.exception(e);
    AnalyticsService.event(Bixby.Constants.APPINSIGHTS_PORTFOLIO_SPREAD_INVALID, {
      spread: value || '',
    });
    return null;
  }
};

export const calculateMultiplier = (numerator: number | null | undefined, denominator: number | null | undefined) => {
  if (
    numerator === null ||
    numerator === undefined ||
    denominator === null ||
    denominator === undefined ||
    denominator === 0
  ) {
    return null;
  } else {
    return (parseFloat(numerator as any) / parseFloat(denominator as any)).toFixed(1) as any;
  }
};

export const calculateMultiplierAsNumber = (
  numerator: number | null | undefined,
  denominator: number | null | undefined
): number | null => {
  let result = calculateMultiplier(numerator, denominator);

  if (result !== null) {
    result = parseFloat(result);
  }

  return result;
};

export const formatByLineDateTime = (value: Date | null) => {
  if (value === null || value === undefined) {
    return '';
  }

  const dayjsDate: dayjs.Dayjs | null = value ? dayjs.utc(value) : null;
  return dayjsDate && dayjsDate.isValid()
    ? `${Bixby.Utils.formatDateForByline(dayjsDate)} at ${Bixby.Utils.formatTimeForByline(dayjsDate)}`
    : null;
};

export const sum: (items: any[], prop: string) => number = (items: any[], prop: string) => {
  const result: number = (items || []).reduce((accumulator: number, currentValue) => {
    return accumulator + (parseInt(currentValue[prop]) || 0);
  }, 0);

  return result;
};

export const getKeys = <T extends object>(o: T): Array<keyof T> => {
  return Object.keys(o) as Array<keyof T>;
};
